import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Github, Linkedin, Mail, ArrowRight, Sparkles, Download, ExternalLink, Code2, Cpu, Layers, PenTool, Globe2, Phone, ChevronRight, Play, Menu, X } from "lucide-react";
import profileImage from "../assets/ChatGPT Image Aug 25, 2025, 05_02_55 PM.png";
import healthGameImage from "../assets/Untitled-2_resized.png";
import NextSpaceImage from "../assets/ChatGPT Image Aug 26, 2025, 03_28_04 PM.png";
import MyKuisImage from "../assets/ChatGPT Image Aug 26, 2025, 03_35_23 PM.png";
import TaskManagementImage from "../assets/ChatGPT Image Aug 26, 2025, 07_13_29 PM.png";
import WebRabbitmoon from "../assets/ChatGPT Image Aug 26, 2025, 03_53_46 PM.png";
import CommentsList from "./CommentsList";
import CommentModal from "./CommentModal";

/**
 * Single-file React portfolio with kinetic, glossy UI & micro-interactions
 * TailwindCSS for styling (no imports needed in this environment)
 * Framer Motion animations (entrances, parallax, morphing blobs, magnetic buttons)
 *
 * Sections:
 * - Navbar (glass, scroll progress)
 * - Hero (gradient blob, rotating rim avatar, staggered text, CTA)
 * - Showcase / Projects (tilt cards, hover reveal)
 * - Skills marquee (auto-scrolling, shimmering chips)
 * - Experience timeline (scroll-linked)
 * - About (stats, feature tiles)
 * - Contact (form + links)
 * - Footer
 */

const skills = [
  "Python", "C#", "TypeScript", "React", "React Native", "Node.js", "Express", "Laravel", "CodeIgniter", "Yii",
  "MySQL", "Firebase", "Cloudinary", "Midtrans", "Unity", "Git", "GitHub", "GitLab"
];

const projects = [
  {
    title: "Web Rabbit Moon Invitation",
    desc: "Web Aplikasi untuk generate undangan nikah (gratis & berbayar) dengan integrasi pembayaran Midtrans dan upload Cloudinary.",
    tags: ["React JS", "Node.js", "Midtrans", "MySQL", "Cloudinary"],
    link: "https://rabbit-moon-invation.netlify.app/",
    image:WebRabbitmoon,
  },
   {
    title: "APK RabbitMoon_Invitation",
    desc: "APK untuk generate undangan nikah (gratis & berbayar) dengan integrasi pembayaran Midtrans dan upload Cloudinary.",
    tags: ["React Native", "Node.js", "Midtrans", "MySQL", "Cloudinary"],
    link: "https://drive.google.com/file/d/1_gUksmWp42rQHYfgq5mEKf-Ek_vtQ5oD/view?usp=sharing",
    
  },
  {
    title: "Web App Task Management System",
    desc: "Web Aplikasi CRUD Project, Task, Role, User, Category menggunakan Laravel sebagai Backend dan react js sebagai frontend.",
    tags: ["Laravel", "MySQL","React js"],
    link: "https://github.com/irawan1212/Task_Management",
    image:TaskManagementImage,
  },
  {
    title: "MyKuisApp",
    desc: "Aplikasi kuis & media sosial dengan posting video/gambar/teks dan kuis mata pelajaran.",
    tags: ["React Native", "TypeScript", "Firebase", "Cloudinary"],
    link: "https://github.com/iraone1/MyKuisApp1",
    image:MyKuisImage,
  },
  {
    title: "Health Education Game",
    desc: "Game edukasi berbasis Unity dengan misi, rintangan, dan boss fight.",
    tags: ["Unity", "C#", "Adobe Illustrator", "Spriter Pro"],
    link: "https://irawan125.itch.io/health-education",
    image: healthGameImage,
  },
  {
    title: "NextSpace",
    desc: "Aplikasi Mobile Penyewaan Ruangan.",
    tags: ["Flutter", "Dart", "Firebase", "Midtrans"],
    link: "https://drive.google.com/file/d/1mNe6_I9vqjC4mCedWF7pvvzbYVv1Gsce/view?usp=sharing",
    image: NextSpaceImage,
  },
];
// Comment interface for Firebase
interface Comment {
  id: string;
  name: string;
  comment: string;
  date: string;
  time: string;
}

const stats = [
  { label: "Proyek", value: "5+" },
  { label: "Klien", value: "1" },
  { label: "Tahun", value: "1" },
  { label: "Repos", value: "5+" },
];

function useMagnet(ref: React.RefObject<HTMLElement>, strength = 0.25) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function onMove(e: MouseEvent) {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - (rect.left + rect.width / 2);
      const y = e.clientY - (rect.top + rect.height / 2);
      el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    }

    function reset() {
      if (!el) return;
      el.style.transform = "translate(0,0)";
    }

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", reset);

    return () => {
      if (!el) return;
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", reset);
    };
  }, [ref, strength]);
}

function TypingText({ text, className }: { text: string; className?: string }) {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!isDeleting && currentIndex < text.length) {
      // Typing forward
      timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 150);
    } else if (!isDeleting && currentIndex === text.length) {
      // Pause at the end, then start deleting
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 2000); // Wait 2 seconds before deleting
    } else if (isDeleting && currentIndex > 0) {
      // Deleting backward
      timeout = setTimeout(() => {
        setDisplayText(prev => prev.slice(0, -1));
        setCurrentIndex(prev => prev - 1);
      }, 100); // Faster deletion
    } else if (isDeleting && currentIndex === 0) {
      // Reset to start typing again
      setIsDeleting(false);
      setDisplayText("");
    }

    return () => clearTimeout(timeout);
  }, [currentIndex, text, isDeleting]);

  return (
    <h1 className={className}>
      {displayText}
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.5, repeat: Infinity }}
        className="inline-block ml-1"
      >
        |
      </motion.span>
    </h1>
  );
}

function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const width = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <motion.div style={{ width }} className="fixed left-0 top-0 h-1 z-[60] bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
  );
}

function GlassNav({ onOpenComment }: { onOpenComment: () => void }) {
  const items = [
    { name: "Home", href: "#home" },
    { name: "Projects", href: "#projects" },
    { name: "Skills", href: "#skills" },
    { name: "Experience", href: "#experience" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ];
  const [open, setOpen] = useState(false);
  return (
    <div className="pointer-events-none fixed top-4 left-0 right-0 z-50">
      <nav className="pointer-events-auto backdrop-blur-xl bg-white/10 dark:bg-black/20 border border-white/20 dark:border-white/10 shadow-lg shadow-black/5 rounded-none px-6 py-3 w-full">
        <div className="flex items-center justify-between">
          {/* Portfolio text on the left */}
          <div className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Portofolio
          </div>

          {/* Hamburger (mobile) */}
          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl hover:bg-white/10 transition"
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Navigation items (desktop) */}
          <ul className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <li key={it.name}>
                <a href={it.href} className="text-sm md:text-[13px] px-3 py-1.5 rounded-xl hover:bg-white/10 transition inline-flex items-center gap-2">
                  {it.name}
                </a>
              </li>
            ))}
            <li>
              <button onClick={onOpenComment} className="text-sm md:text-[13px] px-3 py-1.5 rounded-xl hover:bg-white/10 transition inline-flex items-center gap-2">Comment</button>
            </li>
          </ul>
        </div>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden"
            >
              <ul className="mt-2 grid gap-1">
                {items.map((it) => (
                  <li key={it.name}>
                    <a
                      href={it.href}
                      className="block px-3 py-2 rounded-xl hover:bg-white/10 transition"
                      onClick={() => setOpen(false)}
                    >
                      {it.name}
                    </a>
                  </li>
                ))}
                <li>
                  <button onClick={() => { onOpenComment(); setOpen(false); }} className="block w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 transition">Komentar</button>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
}

function Hero() {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };
  const item = { hidden: { y: 20, opacity: 0 }, show: { y: 0, opacity: 1 } };

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-24">
      {/* Background gradient mesh */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.9 }}
        transition={{ duration: 1.2 }}
        className="pointer-events-none absolute inset-0"
      >
        <motion.div
          className="absolute -top-28 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(168,85,247,0.7), transparent 70%)"
          }}
          animate={{ x: [0, 30, -20, 0], y: [0, -10, 30, 0], scale: [1, 1.05, 0.98, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-32 -right-32 w-[46rem] h-[46rem] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(34,211,238,0.6), transparent 70%)"
          }}
          animate={{ x: [0, -25, 10, 0], y: [0, 25, -20, 0], scale: [1, 0.97, 1.03, 1] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="relative z-10 px-4 md:px-6 lg:px-8 max-w-6xl w-full">
        {/* Top Row: Photo + Content */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="relative flex justify-center md:justify-start order-2 md:order-1"
          >
            <div className="relative w-80 h-80 md:w-96 md:h-96">
              {/* Static professional frame */}
              <div className="absolute inset-0 rounded-full p-[3px] bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500">
                <div className="h-full w-full rounded-full bg-black/80 backdrop-blur-xl flex items-center justify-center">
                  {/* Profile image avatar - no rotation */}
                  <div className="w-[100%] h-[100%] rounded-full bg-gradient-to-br from-white/10 to-white/5 border border-white/20 flex items-center justify-center overflow-hidden shadow-2xl">
                    <img
                      src={profileImage}
                      alt="Muhammad Irawan"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Professional badge */}
              <motion.div
                className="absolute -bottom-4 -left-4"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
              >
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div variants={container} initial="hidden" animate="show" className="text-left order-1 md:order-2">
            <motion.div variants={item} className="mt-4">
              <TypingText
                text="Muhammad Irawan"
                className="text-4xl md:text-6xl font-extrabold leading-[1.1] text-white whitespace-nowrap" />
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
                className="block text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-violet-400 to-cyan-300 text-2xl md:text-5xl font-bold mt-2"
              >
                Fresh Graduate
              </motion.span>
            </motion.div>
            <motion.p variants={item} className="mt-4 text-base/7 text-white/70 max-w-prose">
              Dengan Keahlian di Bidang IT seperti WEB Developer,Mobile Developer dan Game Developer
            </motion.p>
            <motion.div variants={item} className="mt-6 flex flex-wrap gap-3">
              <Magnetic>
                <button className="rounded-2xl px-6 py-6 text-base bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:from-fuchsia-600 hover:to-cyan-600 transition-all duration-300">
                  <a href="#projects" className="inline-flex items-center gap-2">Lihat Proyek <ArrowRight className="w-4 h-4" /></a>
                </button>
              </Magnetic>
              <button className="rounded-2xl px-6 py-2 text-base bg-white/10 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <a href="#contact" className="inline-flex items-center gap-2"><Mail className="w-4 h-4" />Kontak</a>
              </button>
              <button className="rounded-2xl px-6 py-2 text-base hover:bg-white/10 transition-all duration-300">
                <a href="https://drive.google.com/file/d/1aGJ-_h0E79BEWhnBEhq6iiOFSTM7z5gB/view?usp=sharing" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2"><Download className="w-4 h-4" />CV</a>
              </button>
            </motion.div>
            <motion.div variants={item} className="mt-8 flex gap-4">
              <Social href="https://github.com/irawan1212" label="GitHub"><Github className="w-5 h-5" /></Social>
              <Social href="https://www.linkedin.com/in/muhammad-irawan-7aaa652b1" label="LinkedIn"><Linkedin className="w-5 h-5" /></Social>
              <Social href="mailto:irawanm125@gmail.com" label="Email"><Mail className="w-5 h-5" /></Social>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Row: Testimonials + Comments */}
        <div id="comments" className="flex justify-center mt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="group relative w-full max-w-5xl"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
            <div className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-10">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-white/90 mb-2 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Komentar
                </h3>
              </div>
              <CommentsList />
            </div>
          </motion.div>
        </div>
      </div>

      {/* subtle bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
}



function Projects() {
  return (
    <section id="projects" className="relative py-20 px-4 md:px-6 lg:px-8 max-w-6xl mx-auto pt-24">
      <SectionHeader title="Projek" subtitle="Beberapa karya pilihan dengan fokus pada UX, performa, dan animasi." />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((p, index) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <ProjectCard {...p} />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function ProjectCard({ title, desc, tags, link, image }: { title: string; desc: string; tags: string[]; link: string; image?: string }) {
  return (
    <motion.a
      href={link}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative block rounded-3xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-xl h-full"
      style={{ perspective: 1000 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500" />
      <div className="p-6 h-full flex flex-col">
        <div className="relative rounded-2xl overflow-hidden border border-white/15 group-hover:border-cyan-400/30 transition-all duration-300 aspect-[16/9]">
          {image ? (
            <img src={image} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-tr from-fuchsia-500/20 via-violet-500/20 to-cyan-400/20 flex items-center justify-center">
              <Code2 className="w-12 h-12 text-cyan-400 group-hover:scale-110 transition-transform duration-300" />
            </div>
          )}
        </div>
        <div className="mt-5 flex-1">
          <h3 className="text-xl font-bold text-white/90 group-hover:text-white transition-colors duration-300">{title}</h3>
          <p className="text-sm text-white/70 mt-2 leading-relaxed">{desc}</p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {tags.map((t) => (
            <div key={t} className="bg-white/10 border border-white/20 px-3 py-1.5 rounded-lg text-xs font-medium text-white/80 group-hover:bg-cyan-500/20 group-hover:border-cyan-400/30 transition-all duration-300">
              {t}
            </div>
          ))}
        </div>
        <div className="mt-5 inline-flex items-center gap-2 text-sm text-cyan-400 opacity-80 group-hover:opacity-100 group-hover:text-cyan-300 transition-all duration-300">
          Lihat detail <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </div>
    </motion.a>
  );
}

function SkillsMarquee() {
  return (
    <section id="skills" className="py-20 px-4 md:px-6 lg:px-8 max-w-6xl mx-auto pt-24">
      <SectionHeader title="Skill & Teknologi" subtitle="Teknologi yang sering saya gunakan dalam produksi." />
      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {skills.map((skill, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
            className="group relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
            <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/15 hover:border-cyan-400/30 transition-all duration-300 h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 animate-pulse" />
                <span className="text-sm font-semibold text-white/90">{skill}</span>
              </div>
              <div className="text-xs text-white/60 leading-relaxed">
                {skill === "React" && "Frontend Framework untuk UI yang interaktif"}
                {skill === "Next.js" && "Full-stack Framework dengan SSR & SSG"}
                {skill === "TypeScript" && "JavaScript dengan type safety"}
                {skill === "TailwindCSS" && "Utility-first CSS framework"}
                {skill === "Framer Motion" && "Library animasi yang powerful"}
                {skill === "Firebase" && "Backend as a Service dari Google"}
                {skill === "Node.js" && "Runtime environment untuk JavaScript"}
                {skill === "Express" && "Web framework minimalis & fleksibel"}
                {skill === "PostgreSQL" && "Database relational yang robust"}
                {skill === "MongoDB" && "Database NoSQL yang scalable"}
                {skill === "GraphQL" && "Query language untuk API yang efisien"}
                {skill === "Redux" && "State management untuk aplikasi besar"}
                {skill === "Zustand" && "State management yang lightweight"}
                {skill === "React Query" && "Data fetching & caching yang smart"}
                {skill === "Jest" && "Testing framework untuk JavaScript"}
                {skill === "Playwright" && "E2E testing yang reliable"}
                {skill === "Docker" && "Containerization untuk deployment"}
                {skill === "CI/CD" && "Pipeline automation untuk DevOps"}
                {skill === "Figma" && "Design tool untuk UI/UX"}
                {skill === "Three.js" && "3D graphics library untuk web"}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function SparkleDot() {
  return (
    <span className="relative inline-flex">
      <span className="w-2 h-2 rounded-full bg-white/80" />
      <span className="absolute inset-0 -z-10 blur-sm bg-white/50 rounded-full" />
    </span>
  );
}

function CommentCard({ name, comment, date, time }: { name: string; comment: string; date: string; time: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group relative"
    >
      <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
      <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/15 hover:border-cyan-400/30 transition-all duration-300 h-full">
        <div className="flex items-center gap-5 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-xl">
            {name.charAt(0)}
          </div>
          <div>
            <div className="font-bold text-white/90 text-xl mb-1">{name}</div>
            <div className="text-sm text-white/60 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400"></span>
              {date} • {time}
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -left-3 top-0 w-1.5 h-full bg-gradient-to-b from-cyan-400/60 to-purple-400/60 rounded-full"></div>
          <p className="text-base text-white/80 leading-relaxed pl-6 italic font-medium">"{comment}"</p>
        </div>
      </div>
    </motion.div>
  );
}

function TestimonialsSlider() {
  const testimonials = [
    {
      name: "Ahmad Rizki",
      comment: "Portfolio yang sangat menarik dan profesional! Animasi yang smooth dan design yang modern. Sangat impressed dengan skill yang dimiliki.",
      date: "15 Jan 2025",
      time: "14:30",
      role: "UI/UX Designer"
    },
    {
      name: "Sarah Putri",
      comment: "Skill yang lengkap dan proyek yang berkualitas tinggi. Sangat recommended untuk hire! Komunikasi yang baik dan hasil yang memuaskan.",
      date: "12 Jan 2025",
      time: "09:15",
      role: "Project Manager"
    },
    {
      name: "Budi Santoso",
      comment: "Developer yang handal dengan UI/UX yang excellent. Proyek yang dihasilkan sangat memuaskan dan sesuai dengan ekspektasi.",
      date: "10 Jan 2025",
      time: "16:45",
      role: "Tech Lead"
    },
    {
      name: "Diana Kusuma",
      comment: "Kualitas kerja yang outstanding! Sangat detail oriented dan selalu deliver tepat waktu. Highly recommended untuk project development.",
      date: "8 Jan 2025",
      time: "11:20",
      role: "Product Owner"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="relative">
      {/* Testimonials Container */}
      <div className="overflow-hidden">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="flex justify-center"
        >
          <div className="w-full max-w-4xl">
            <CommentCard
              name={testimonials[currentIndex].name}
              comment={testimonials[currentIndex].comment}
              date={testimonials[currentIndex].date}
              time={testimonials[currentIndex].time}
            />
          </div>
        </motion.div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-3 mt-10">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-3 rounded-full transition-all duration-500 ${index === currentIndex
                ? 'w-10 bg-gradient-to-r from-cyan-400 to-purple-400'
                : 'w-3 bg-white/30 hover:bg-white/50 hover:w-6'
              }`}
          />
        ))}
      </div>
    </div>
  );
}

function Timeline() {
  const items = [
    {
      icon: <Cpu className="w-5 h-5" />,
      title: "Game Developer Intern — PT. Media Kreasi Abadi (MSIB)",
      time: "Aug 2022 – Dec 2022",
      desc: (
        <ul className="list-disc ml-5 space-y-1 text-sm text-white/70">
          <li>Merancang dan membuat aset 2D isometrik dengan gaya seni konsisten.</li>
          <li>Mengembangkan animasi karakter, interaksi lingkungan, dan efek khusus.</li>
          <li>Merancang mekanisme permainan & level design bersama tim.</li>
          <li>Menggunakan Adobe Illustrator, Spriter Pro, dan Unity untuk integrasi aset.</li>
          <li>Menjadi <strong>Point of Contact (PIC)</strong> tim seni & animasi, memastikan progress tepat waktu.</li>
          <li>Membuat pustaka aset & animasi reusable untuk efisiensi proyek selanjutnya.</li>
        </ul>
      ),
    },
    {
      icon: <Layers className="w-5 h-5" />,
      title: "Mobile Developer Intern — Akselera Tech",
      time: "Jun 2025 – Aug 2025",
      desc: (
        <ul className="list-disc ml-5 space-y-1 text-sm text-white/70">
          <li>Mengembangkan aplikasi mobile sesuai kebutuhan client.</li>
          <li>Membuat UI/UX dari desain Figma tim.</li>
          <li>Mengimplementasikan integrasi data dengan Firestore Firebase.</li>
          <li>Mengintegrasikan payment gateway <strong>Midtrans</strong>.</li>
          <li>Tools: Flutter, Firebase Authentication, Firestore, Midtrans API, GitHub.</li>
        </ul>
      ),
    },

  ];

  const { scrollYProgress } = useScroll();
  const scaleY = useTransform(scrollYProgress, [0, 1], [0.1, 1]);

  return (
    <section id="experience" className="py-20 px-4 md:px-6 lg:px-8 max-w-6xl mx-auto pt-24">
      <SectionHeader title="Pengalaman" subtitle="Perjalanan profesional dalam beberapa tahun terakhir." />
      <div className="relative mt-12 grid md:grid-cols-[auto_1fr] gap-8">
        <motion.div style={{ scaleY }} className="origin-top hidden md:block w-1 rounded-full bg-gradient-to-b from-cyan-400 via-purple-500 to-pink-500" />
        <div className="space-y-6">
          {items.map((it, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: idx * 0.1 }}
              className="group relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
              <div className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-6 hover:border-cyan-400/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-400/30">
                    {it.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white/90">{it.title}</h3>
                    <div className="mt-2">{it.desc}</div>
                  </div>
                  <div className="text-xs text-cyan-400 font-medium bg-cyan-500/10 px-3 py-1.5 rounded-full border border-cyan-400/20">
                    {it.time}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {

  return (
    <section id="about" className="py-20 px-4 md:px-6 lg:px-8 max-w-6xl mx-auto pt-24">
      <SectionHeader title="Tentang Saya" subtitle="Sedikit tentang saya." />
      <div className="mt-12 grid lg:grid-cols-2 gap-10">
        {/* Left: Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="group relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
          <div className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-8">
            <div className="space-y-4 text-white/80 text-sm leading-relaxed">
              <p>
                Saya lulusan baru dari Universitas 'Aisyiyah Yogyakarta (S.Kom, Teknologi Informasi, konsentrasi Rekayasa Perangkat Lunak) dengan IPK 3,12.
              </p>
              <p>
                Memiliki pengalaman enam bulan melalui program MSIB sebagai Game Developer, terbiasa berkolaborasi lintas fungsi untuk merancang, membangun, dan mengoptimalkan produk digital.
              </p>
              <p>
                Fokus pada pengembangan aplikasi web, mobile, dan game dengan perhatian pada kualitas, performa, dan pengalaman pengguna. Siap berkontribusi melalui solusi yang inovatif dan berkualitas.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right: Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="group relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
          <div className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-8 h-full">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl bg-white/10 border border-white/20 p-6 text-center hover:bg-white/15 transition-all duration-300">
                  <div className="text-2xl font-extrabold text-cyan-400">{s.value}</div>
                  <div className="text-xs text-white/70 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  useMagnet(ref, 0.18);
  return (
    <section id="contact" className="py-20 px-6 md:px-10 lg:px-16 max-w-6xl mx-auto pt-24">
      <SectionHeader title="Kontak Pribadi Saya" subtitle="Anda bisa menghubungi saya melalui kontak berikut." />
      <div className="mt-10">
        <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div ref={ref} className="relative">
              <a href="https://wa.me/6281477182186" target="_blank" rel="noreferrer" className="group flex items-center justify-between rounded-2xl border border-white/15 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-cyan-400/20 px-4 py-4 hover:from-green-500/30 hover:to-cyan-400/30">
                <span className="inline-flex items-center gap-2 text-green-400">
                  <svg viewBox="0 0 32 32" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                    <path d="M16 3c-7.18 0-13 5.82-13 13 0 2.29.6 4.45 1.65 6.31L3 29l6.85-1.79C11.64 28.24 13.76 29 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23c-1.88 0-3.62-.55-5.08-1.5l-.36-.23-4.02 1.05 1.07-3.92-.24-.38A9.94 9.94 0 0 1 6 16c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10z" />
                    <path d="M19.11 17.53c-.27-.13-1.6-.79-1.85-.88-.25-.09-.43-.13-.62.13-.18.27-.71.88-.87 1.06-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.34-.8-.71-1.34-1.58-1.5-1.85-.16-.27-.02-.42.11-.55.11-.11.27-.29.4-.44.13-.15.18-.25.27-.42.09-.18.04-.33-.02-.46-.07-.13-.62-1.49-.85-2.03-.22-.53-.44-.46-.62-.46-.16 0-.33-.02-.5-.02-.18 0-.46.07-.7.33-.24.27-.92.9-.92 2.2s.94 2.55 1.07 2.73c.13.18 1.85 2.82 4.48 3.95.63.27 1.13.43 1.52.55.64.2 1.22.18 1.68.11.51-.08 1.6-.65 1.83-1.28.22-.64.22-1.18.15-1.28-.07-.11-.24-.18-.51-.31z" />
                  </svg>
                  WhatsApp: 6281477182186
                </span>
                <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition" />
              </a>
            </div>
            <div ref={ref} className="relative">
              <a href="mailto:irawanm125@gmail.com" className="group flex items-center justify-between rounded-2xl border border-white/15 bg-gradient-to-r from-fuchsia-500/20 via-violet-500/20 to-cyan-400/20 px-4 py-4 hover:from-fuchsia-500/30 hover:to-cyan-400/30">
                <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" />Email: irawanm125@gmail.com</span>
                <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition" />
              </a>
            </div>
            <div ref={ref} className="relative">
              <a href="tel:081477182186" className="group flex items-center justify-between rounded-2xl border border-white/15 bg-gradient-to-r from-sky-500/20 via-violet-500/20 to-cyan-400/20 px-4 py-4 hover:from-sky-500/30 hover:to-cyan-400/30">
                <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" />Telepon: 081477182186</span>
                <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex items-end justify-between gap-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-extrabold">{title}</h2>
      </div>

    </div>
  );
}

function Input({ label, ...props }: { label: string;[key: string]: any }) {
  return (
    <div>
      <label className="text-sm opacity-80">{label}</label>
      <input {...props} className="mt-2 w-full rounded-2xl bg-black/30 border border-white/15 p-4 outline-none focus:ring-2 focus:ring-fuchsia-400/40" />
    </div>
  );
}

function Social({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
  return (
    <a href={href} aria-label={label} className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition">
      {children}
    </a>
  );
}

function Magnetic({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useMagnet(ref, 0.12);
  return <div ref={ref} className="inline-block">{children}</div>;
}

function Footer() {
  return (
    <footer className="py-10 text-sm text-center opacity-70">
      © {new Date().getFullYear()} Muhammad Irawan — Dibuat dengan React, Tailwind, & Framer Motion.
    </footer>
  );
}

export default function Portofolio() {
  // background dots grid
  const [commentOpen, setCommentOpen] = useState(false);
  return (
    <div className="min-h-screen text-white bg-[#0b0b12] [background-image:radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:22px_22px] [background-position:0_0,11px_11px]">
      <ProgressBar />
      <GlassNav onOpenComment={() => setCommentOpen(true)} />
      <Hero />
      <Timeline />

      <Projects />
      <SkillsMarquee />
      <About />
      <Contact />
      <CommentModal open={commentOpen} onClose={() => setCommentOpen(false)} />
      <Footer />
    </div>
  );
}